/**
 * Query layer over a sample skeleton: span-by-begin-ordinal, ancestor
 * stacks, and filter visibility — all answered from span extents and
 * counters with zero event reads (sticky headers and depth seeding for the
 * decode walk).
 */
import { at } from "./format";
import type { SampleSkeleton, SkeletonSpan } from "./types";

// `parent` is typed number but arrives via JSON.parse of a log-authored
// sidecar. Anything other than an earlier span's index would index
// `childrenOf` through its prototype (a "__proto__" parent reaches
// Array.prototype.push), so the value is checked before it is used as a key.
const parentIndex = (parent: unknown, i: number): number | undefined => {
  if (parent === undefined) {
    return undefined;
  }
  if (
    typeof parent !== "number" ||
    !Number.isInteger(parent) ||
    parent < 0 ||
    parent >= i
  ) {
    throw new Error(
      `Skeleton span ${i} has an invalid parent ${JSON.stringify(parent)}`
    );
  }
  return parent;
};

export class SkeletonIndex {
  readonly spans: SkeletonSpan[];
  readonly childrenOf: number[][];
  readonly roots: number[];
  readonly spanIds: ReadonlySet<string>;
  private byBegin = new Map<number, number>();

  constructor(readonly skeleton: SampleSkeleton) {
    this.spans = skeleton.spans;
    this.childrenOf = this.spans.map(() => []);
    this.roots = [];
    this.spans.forEach((span, i) => {
      this.byBegin.set(span.begin, i);
      const parent = parentIndex(span.parent, i);
      if (parent === undefined) {
        this.roots.push(i);
      } else {
        at(this.childrenOf, parent).push(i);
      }
    });
    this.spanIds = new Set(this.spans.map((span) => span.id));
  }

  /** Span whose begin event sits at `ordinal` (undefined: dissolved span). */
  spanAtBegin(ordinal: number): number | undefined {
    return this.byBegin.get(ordinal);
  }

  /**
   * Structural ancestor stack (outermost first) containing `ordinal` —
   * answered entirely from extents; the span_begin events need never be
   * fetched. Interleaved-span extent overlap: first containing child wins
   * (tolerated per spec; correctness comes from span_id on fetched events).
   */
  spanStackAt(ordinal: number): number[] {
    const stack: number[] = [];
    let candidates = this.roots;
    for (;;) {
      const hit = candidates.find((i) => {
        const [lo, hi] = at(this.spans, i).extent;
        return lo <= ordinal && ordinal <= hi;
      });
      if (hit === undefined) {
        return stack;
      }
      stack.push(hit);
      candidates = at(this.childrenOf, hit);
    }
  }

  depthAt(ordinal: number): number {
    return this.spanStackAt(ordinal).length;
  }

  /** Does an expanded span have anything to show under the current filter? */
  hasVisibleContents(
    spanIdx: number,
    visibleTypes: (type: string) => boolean
  ): boolean {
    // No `models` fast path needed: a direct-child model shows up in
    // `children.model`, and a deeper one implies a structural child span
    // (dissolved leaves require zero aggregate models — see skeleton.ts).
    const span = at(this.spans, spanIdx);
    if (
      Object.entries(span.children).some(
        ([type, count]) => count > 0 && visibleTypes(type)
      )
    ) {
      return true;
    }
    return at(this.childrenOf, spanIdx).length > 0;
  }
}
